import {
  CompanyType,
  ConversationStatus,
  ConversationPriority,
  Direction,
  Source,
} from "@lytra/frontend-common/__generated__/graphql";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Check,
  Sparkles,
  Clock,
  Forward,
  User,
  Building,
  MapPin,
  Phone,
  Send,
  Reply,
  Paperclip,
  RotateCw,
  Bot,
  Lock,
  Languages,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  useApolloClient,
  useMutation,
  useQuery,
  useSubscription,
  useSuspenseQuery,
} from "@apollo/client/react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { getAttachmentIcon } from "@/lib/utils";
// Tiptap removed - ticket system not in use
// import { MinimalTiptapEditor } from "../../minimal-tiptap";
// import { Editor } from "@tiptap/react";
// Stands in for the removed Tiptap `Editor`. `emitUpdate` is the second argument
// Tiptap's own setContent takes, and a call site here passes it.
interface Editor {
  commands?: { setContent: (content: string, emitUpdate?: boolean) => void };
}
import PanelNavigation from "../panel/PanelNavigation";
import ConversationHeader, {
  Action,
  InfoField,
} from "@/components/customer/ConversationHeader";
import { COMPANY_TYPE } from "@/consts/lang";
import SidePanel from "@/components/customer/panel/SidePanel";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { useToast } from "@/hooks/use-toast";
import { DateTime } from "luxon";
import { Badge } from "@/components/ui/badge";
import { Badge as BadgeV2 } from "@lytra/frontend-common/components/v2/ui/badge";
import {
  useProfile,
  useFeature,
  useFeatureEnabled,
} from "@/providers/ProfileProvider";
import { useWorkingColleague } from "@/hooks/useAgentPresence";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { cn, formatDateTime } from "@/lib/utils";
import { useFileUpload } from "@/providers/FileUploadProvider";
import {
  CONVERSATION_WITH_MESSAGES_QUERY,
  SET_READ_MUTATION,
  RECORD_VIEW_MUTATION,
  GET_AGENTS,
  GET_TEAMS,
  REPLY_MUTATION,
  REANNOTATE_MESSAGE_MUTATION,
} from "./ticketQueries";
import {
  type ConversationChange,
  assigneeOptions,
  assigneeValue,
  formatEmailContact,
  subjectDiverges,
  convertPlaintextToTiptapHTML,
  getMessageContent,
  isEnumMember,
  parseAssigneeValue,
  personName,
  formatFileSize,
  PRIORITY_ORDER,
  priorityName,
} from "./ticketHelpers";
import {
  choiceOf,
  statusChange,
  statusChoiceValue,
  statusChoices,
  ticketStatusName,
  useTicketStatuses,
} from "./ticketStatuses";
import { useConversationChange } from "./useConversationChange";
import {
  useAssignToMeShortcut,
  useOwnershipToasts,
  useTicketOwnerUpdates,
} from "./useTicketOwnership";
import { useShowMessage } from "./useShowMessage";
import { useAttachmentDownload } from "./useAttachmentDownload";
import MessageContent from "./MessageContent";
import { NoteComposer } from "./NoteComposer";
import { TICKET_NOTE_UPDATES } from "./noteQueries";
import { NoteThread, ShareReplyBubble } from "./NoteThread";
import { useForwardHost } from "./ForwardHost";
import {
  forwardSourceOf,
  groupTimeline,
  isInternalRow,
  lastCustomerReplyTarget,
  spreadShareReplies,
} from "./noteThreads";
import { THREAD_ROW_WIDTH, THREAD_SIDE_CLASS, threadSide } from "./threadSide";
import {
  affectsTicketThread,
  announcementFor,
  isThreadMessage,
  takeFreshThreadMessages,
} from "./liveReplies";
import { scrollMessageToCenter } from "./messageScroll";
import CollapsibleItems from "./CollapsibleItems";
import SenderName, { RecipientNames } from "./SenderName";
import RelativeTime from "@/components/RelativeTime";
import {
  DeliveryProblemBadge,
  DeliveryProblemNotice,
} from "./DeliveryProblemNotice";
import TicketMetaHeader from "./TicketMetaHeader";
import {
  AnswerLanguageSelect,
  answerLanguageLabel,
  Throbber,
  useRecordActivity,
} from "@lytra/frontend-common";
import { useThreadTranslation } from "./useThreadTranslation";
import { FUER_DICH_OPENED } from "@/components/notifications/notificationQueries";

const DEFAULT_LAYOUT = [undefined, 20];
const DEFAULT_COMPOSE_LAYOUT = [undefined, 40];

interface TicketProps {
  ticketId: string;
  hideEditor?: boolean;
  /** Scroll to and briefly highlight this message after it renders. */
  highlightedMessageId?: string;
  /** Offset inbound/outbound messages to opposite sides, chat-style, instead of
   * the full-width rows `hideEditor` otherwise renders. */
  chatBubbleLayout?: boolean;
  /** With `hideEditor`: show the ticket meta header (status, priority, assignee,
   * attachments) instead of the plain conversation header. */
  metaHeader?: boolean;
  /** Renders the header (meta or plain) into this element, above the surrounding
   * columns, instead of on top of the thread. */
  headerSlot?: HTMLElement | null;
  /** Service-assistant context: sender and recipient show names only (the
   * address on click), since the service side is clear from the ticket. */
  hideAddresses?: boolean;
  /** Shows the note composer and the reply actions of note threads. Off in dialogs and archive views. */
  enableNotes?: boolean;
}

export default function Ticket({
  ticketId,
  hideEditor,
  highlightedMessageId,
  chatBubbleLayout,
  metaHeader,
  headerSlot,
  hideAddresses,
  enableNotes,
}: TicketProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const renderInSlot = (header: ReactNode) =>
    headerSlot ? createPortal(header, headerSlot) : header;
  // A click on a "Für dich" row for the ticket that is already open navigates to the same
  // path; the location key changes then, which re-runs the scroll to the deep-linked message.
  const { key: locationKey } = useLocation();
  const { user, tenant, userRoles } = useProfile();
  const connectorHealthBadgeEnabled = useFeature("connector-health-badge");
  const [showSmartHistory, setShowSmartHistory] = useState(false);
  const [showAfterSales, setShowAfterSales] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const editorRef = useRef<Editor | null>(null);
  const [showFullContent, setShowFullContent] = useState<
    Record<string, boolean>
  >({});
  const [showAttachments, setShowAttachments] = useState(false);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState<string>("");
  const [loadRemoteImages, setLoadRemoteImages] = useState<
    Record<string, boolean>
  >({});
  const [replyToAll, setReplyToAll] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [replyToForwarder, setReplyToForwarder] = useState(false);
  const {
    handleSelectAttachments,
    selectedFiles,
    getSuccessfulAttachmentIds,
    removeFile,
  } = useFileUpload();

  // Adjust state during render instead of an effect: seeds the selection
  // once tenant.serviceEmailAddress/user.emailAddress become available,
  // without an extra render pass. The user can still override afterward.
  const [prevEmailSource, setPrevEmailSource] = useState([
    tenant.serviceEmailAddress,
    user.emailAddress,
  ]);
  if (
    tenant.serviceEmailAddress !== prevEmailSource[0] ||
    user.emailAddress !== prevEmailSource[1]
  ) {
    setPrevEmailSource([tenant.serviceEmailAddress, user.emailAddress]);
    if (tenant.serviceEmailAddress) {
      setSelectedEmailAddress(tenant.serviceEmailAddress);
    } else if (user.emailAddress) {
      // Fallback to user email if no service email is available
      setSelectedEmailAddress(user.emailAddress);
    }
  }

  // The Apollo client defaults to errorPolicy "all", so a failed query does not
  // throw: it returns no data and an `error`. Keep the error, otherwise a server
  // fault reads as "ticket not found".
  const {
    data: ticketData,
    error: ticketError,
    refetch: refetchTicket,
  } = useSuspenseQuery(CONVERSATION_WITH_MESSAGES_QUERY, {
    variables: { conversationId: ticketId },
    // A reopened ticket shows its cached thread at once and picks up what arrived since.
    fetchPolicy: "cache-and-network",
  });

  const conversation = ticketData?.conversationOne;
  // Only `conversationOne` is nullable — its `messages` connection and `edges` list are
  // not — so the sole reason there could be no list is a conversation that does not
  // exist, and that has no messages by definition. Keeping this a real (possibly empty)
  // array is what lets the render path below the not-found guard index into it without
  // pretending it might be missing.
  const messages = useMemo(
    () => conversation?.messages.edges.map((edge) => edge.node) ?? [],
    [conversation],
  );
  // Notes and their replies are grouped into threads that sit where their root sits (D-12, D-13).
  const timeline = useMemo(() => groupTimeline(messages), [messages]);
  const translationEnabled = useFeature("message-translation");
  const threadTranslation = useThreadTranslation(
    ticketId,
    conversation?.languageCode,
    messages.length,
    translationEnabled,
  );
  // A share's replies are shown on their own, at their own time (#717).
  const threadRows = useMemo(
    () => spreadShareReplies(messages, timeline),
    [messages, timeline],
  );
  // The share whose reply composer is open: its card holds it, a reply's "Antworten" opens it.
  const [replyingShareId, setReplyingShareId] = useState<string | null>(null);

  // Fire-and-forget: record that this ticket was viewed in lytra so BI can
  // compare viewed tickets against used proposed answers. Anchored on the
  // latest message in the conversation that is not a note (the agent_activity_event
  // stream is message-anchored; the conversation is derivable via message.conversationId).
  // A note is never the anchor (D-17).
  const latestMessageId = messages.findLast((m) => !m.isNote)?.id;
  const [recordView] = useMutation(RECORD_VIEW_MUTATION);
  const activity = useRecordActivity("VENUS");
  useEffect(() => {
    if (latestMessageId) {
      recordView({ variables: { messageId: latestMessageId } }).catch(
        (error: unknown) => {
          console.error("Failed to record view", error);
        },
      );
    }
  }, [latestMessageId, recordView]);

  // Opening a ticket closes the user's own open "Für dich" update on it (#807). Keyed on the
  // conversation id, not on `conversation` (a new object on every refetch), and independent of the
  // `isRead` effect below: the shared read flag is false only for the first viewer, so it cannot
  // close a per-user update. Fire and forget; the server closes only the caller's own entry.
  const [notifyOpened] = useMutation(FUER_DICH_OPENED);
  const openedConversationId = conversation?.id;
  const mayHaveFuerDich = userRoles.isAgent || userRoles.isAdmin;
  useEffect(() => {
    if (!openedConversationId || !mayHaveFuerDich) return;
    notifyOpened({ variables: { conversationId: openedConversationId } }).catch(
      (error: unknown) => {
        console.error("Failed to close the Für-dich update", error);
      },
    );
  }, [openedConversationId, mayHaveFuerDich, notifyOpened]);

  const [replyToMessageId, setReplyToMessageId] = useState<string | undefined>(
    () => lastCustomerReplyTarget(messages),
  );

  // Transient highlight for a referenced message — fades after ~2.5s so the
  // user notices what was deep-linked without permanently colorizing a row.
  const [transientHighlightId, setTransientHighlightId] = useState<
    string | null
  >(null);
  // Genuinely synchronizing with the DOM (scrollIntoView) and an external
  // timer (setTimeout) here, not deriving from props/state.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!highlightedMessageId) return;
    setTransientHighlightId(highlightedMessageId);
    // The id comes from the URL: CSS.escape keeps a crafted value from changing the selector
    // (T-02-23). The element may appear a frame later (a note thread opens on the same pass
    // that sets the highlight), so look for it for up to 10 frames; an unknown id does nothing.
    let frame = 0;
    let attempts = 0;
    const seek = () => {
      if (scrollMessageToCenter(highlightedMessageId)) return;
      if (++attempts < 10) frame = requestAnimationFrame(seek);
    };
    frame = requestAnimationFrame(seek);
    const t = setTimeout(() => setTransientHighlightId(null), 2500);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(t);
    };
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [highlightedMessageId, locationKey]);

  // "Anzeigen" on a send entry: scrolls to the forwarded message and rings it for a moment.
  const showMessage = useShowMessage(setTransientHighlightId);
  // The quote line of a send entry: who wrote the forwarded message and when, or null when it
  // is not in the loaded thread.
  const describeForwardSource = (messageId: string) => {
    const source = forwardSourceOf(messages, messageId);
    if (!source) return null;
    return {
      sender: source.sender ?? t("tickets.meta.unknown"),
      at: formatDateTime(source.time),
    };
  };

  const [reply] = useMutation(REPLY_MUTATION, {
    onError: (error) => {
      setIsSending(false);
      toast({
        title: "Fehler beim Senden",
        description: error.message,
        variant: "destructive",
      });
    },
    onCompleted: () => {
      setIsSending(false);
      toast({
        title: "Nachricht gesendet",
        description: "Ihre Antwort wurde erfolgreich versendet.",
      });
      setInputMessage("");
      editorRef.current?.commands?.setContent("");
      selectedFiles.forEach(
        (file) => file.attachmentId && removeFile(file.attachmentId),
      );
    },
    refetchQueries: ["Ticket"],
  });

  const handleSendMessage = () => {
    if (!replyToMessageId) return;

    setIsSending(true);
    void reply({
      variables: {
        messageId: replyToMessageId,
        content: inputMessage,
        replyToAll: replyToAll,
        replyToForwarder: replyToForwarder,
        fromEmailAddress: selectedEmailAddress,
        attachmentIds: getSuccessfulAttachmentIds(),
      },
    });
  };

  const handleSetInputMessage = (newMessage: string) => {
    editorRef.current?.commands?.setContent(
      convertPlaintextToTiptapHTML(newMessage),
      true,
    );
  };

  // Not fetched where it is not shown (only the ticketing header uses it).
  // Errors are tolerated: the thread must render even if the directory is
  // unreachable.
  const {
    data: agentsData,
    loading: agentsLoading,
    error: agentsError,
  } = useQuery(GET_AGENTS, {
    errorPolicy: "all",
    skip: hideEditor && !metaHeader && !enableNotes,
  });
  // The recipient field of "An Kollegen senden" tells "in Lytra" from "per Mail" by this list.
  const forwardAgentsState: "loading" | "ready" | "error" =
    agentsData?.agents !== undefined
      ? "ready"
      : agentsError && !agentsLoading
        ? "error"
        : "loading";
  const { data: teamsData } = useQuery(GET_TEAMS, {
    errorPolicy: "all",
    skip: hideEditor && !metaHeader,
  });
  const updateConversation = useConversationChange(
    agentsData?.agents ?? [],
    teamsData?.teams ?? [],
    user.id,
  );
  const ticketStatuses = useTicketStatuses();

  // A colleague's note, send or mail reply arrives through the tenant-wide `messages`
  // subscription: refetch this ticket only for a note, a send or an internal row of this
  // conversation (T-02-27, T-03-39, #717). The refetch goes through the client (not the suspense
  // hook's `refetch`), so the ticket never falls back to its loading state; nothing scrolls,
  // focus never moves, and an open composer keeps its state.
  const client = useApolloClient();
  const [noteAnnouncement, setNoteAnnouncement] = useState("");
  // Thread messages already announced or seen: a second event must not announce the same one twice.
  const seenNoteIdsRef = useRef<Set<string> | null>(null);
  const announceNewNotes = async () => {
    const known = (seenNoteIdsRef.current ??= new Set(
      messages.filter(isThreadMessage).map((m) => m.id),
    ));
    await client.refetchQueries({
      include: [CONVERSATION_WITH_MESSAGES_QUERY],
    });
    const after = client
      .readQuery({
        query: CONVERSATION_WITH_MESSAGES_QUERY,
        variables: { conversationId: ticketId },
      })
      ?.conversationOne?.messages.edges.map((edge) => edge.node);
    const fresh = takeFreshThreadMessages(after ?? [], known);
    const announcement = announcementFor(fresh, user.id, (m) =>
      personName(m.agent ?? undefined),
    );
    if (!announcement) return;
    setNoteAnnouncement(
      t(`tickets.notes.status.${announcement.key}`, {
        name: announcement.name ?? t("tickets.notes.unknownAuthor"),
      }),
    );
  };
  useSubscription(TICKET_NOTE_UPDATES, {
    skip: !enableNotes,
    onData: ({ data: result }) => {
      if (affectsTicketThread(result.data?.messages, ticketId)) {
        void announceNewNotes().catch((error: unknown) => {
          console.error("Failed to refresh the ticket after a note", error);
        });
      }
    },
  });

  // Mention chips and note authors resolve a user id to a name through this map; an id the
  // directory does not know falls back to the token's own label (NoteBody).
  // "Mit Kollegen teilen": one button at the thread's foot for the newest shareable message,
  // and the inline composer that takes its place.
  const forwardHost = useForwardHost({
    messages,
    conversationId: conversation?.id,
    agents: agentsData?.agents ?? [],
    agentsState: forwardAgentsState,
    enableNotes,
  });
  const agentsById = useMemo(
    () => new Map((agentsData?.agents ?? []).map((agent) => [agent.id, agent])),
    [agentsData],
  );

  const handleToggleSmartHistory = () => {
    setShowSmartHistory(!showSmartHistory);
  };

  const handleToggleAfterSales = () => {
    setShowAfterSales(!showAfterSales);
  };

  const toggleMessageContent = (messageId: string) => {
    setShowFullContent((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  const { downloadAttachment: handleDownloadAttachment, loadingAttachmentIds } =
    useAttachmentDownload();

  const handleToggleAttachments = () => {
    // Opening the panel is the agent switching to it; closing it is not recorded.
    if (!showAttachments && latestMessageId) {
      activity.tabSwitched(latestMessageId, "attachments");
    }
    setShowAttachments(!showAttachments);
  };

  // Effects
  const [setRead] = useMutation(SET_READ_MUTATION);
  const [reannotateMessage, { loading: reannotateLoading }] = useMutation(
    REANNOTATE_MESSAGE_MUTATION,
    {
      onCompleted: () => {
        toast({
          title: "Nachricht wird neu annotiert",
          description: "Die KI-Analyse wurde neu gestartet.",
        });
      },
      onError: (error) => {
        toast({
          title: "Fehler",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
  useEffect(() => {
    if (conversation?.isRead === false) {
      void setRead({
        variables: { conversationId: conversation.id },
      });
    }
  }, [conversation, setRead]);

  // Who answers the ticket (groupware presence, ticketing only): the owner follows live,
  // the header says when a colleague is working on the answer, "I" assigns it to me, and
  // an owner change made elsewhere is told in a toast.
  const ownershipEnabled =
    useFeatureEnabled("groupware-presence", false) && !!metaHeader;
  useTicketOwnerUpdates(ownershipEnabled);
  const workingColleague = useWorkingColleague(
    ownershipEnabled ? conversation?.id : null,
  );
  const agentName = (userId: string) =>
    personName(agentsData?.agents.find((agent) => agent.id === userId));
  const assignTo = (agentId: string | null) => {
    if (conversation)
      void updateConversation(conversation, { agentId, teamId: null });
  };
  useOwnershipToasts({
    enabled: ownershipEnabled,
    conversationId: ticketId,
    ownerId: conversation?.assignedAgent?.id ?? null,
    assignedById: conversation?.assignedById ?? null,
    currentUserId: user.id,
    nameOf: agentName,
    reassign: assignTo,
  });
  const canAssignToMe =
    ownershipEnabled &&
    !!conversation &&
    conversation.assignedAgent?.id !== user.id &&
    !!agentsData?.agents.some((agent) => agent.id === user.id);
  // The shortcut's handler only changes with whether it applies; it assigns with the
  // conversation as it is when the key is pressed.
  const assignToRef = useRef(assignTo);
  useEffect(() => {
    assignToRef.current = assignTo;
  });
  useAssignToMeShortcut(
    useMemo(
      () => (canAssignToMe ? () => assignToRef.current(user.id) : null),
      [canAssignToMe, user.id],
    ),
  );

  // Render Conditions
  if (!conversation && ticketError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md space-y-3">
          <h2 className="text-xl font-bold">
            Ticket konnte nicht geladen werden
          </h2>
          <p className="text-muted-foreground">
            Beim Laden ist ein Fehler aufgetreten. Das Ticket existiert
            möglicherweise trotzdem.
          </p>
          <p className="text-xs text-muted-foreground break-words">
            {ticketError.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetchTicket()}
          >
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }
  if (!conversation) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">Ticket nicht gefunden</h2>
          <p className="text-muted-foreground">
            Das angeforderte Ticket konnte nicht gefunden werden.
          </p>
        </div>
      </div>
    );
  }

  // Declared below the not-found guard: every one of these reads
  // `conversation` directly, and all of them are only reachable from the JSX
  // rendered past the guard. Above it, TypeScript cannot narrow the captured
  // const, which is what made `conversation.id` and the header fields' ids
  // possibly-undefined.

  // Optimistic, and a failure is reported (useConversationChange).
  const changeConversation = (change: ConversationChange) => {
    void updateConversation(conversation, change);
  };

  const handleCloseTicket = () => {
    changeConversation({ status: ConversationStatus.Closed });
  };

  const headerActions: Action[] = [
    {
      id: "close",
      label: "Schließen",
      icon: Check,
      onClick: handleCloseTicket,
    },
  ];

  const assignees = assigneeOptions(
    agentsData?.agents ?? [],
    teamsData?.teams ?? [],
    user.id,
    t("tickets.meta.unassigned"),
  );
  const headerInfoFields: InfoField[] = [
    {
      id: "assignee",
      value:
        conversation.assignedTeam?.name ??
        personName(conversation.assignedAgent ?? undefined) ??
        undefined,
      valueId: assigneeValue(assignees, {
        agentId: conversation.assignedAgent?.id,
        teamId: conversation.assignedTeam?.id,
      }),
      label: "Zuständig",
      options: assignees,
      onSelect: (value) => {
        changeConversation(parseAssigneeValue(value));
      },
    },
    {
      id: "priority",
      value: conversation ? priorityName(t, conversation.priority) : undefined,
      valueId: conversation?.priority,
      label: "Priorität",
      options: PRIORITY_ORDER.map((priority) => ({
        value: priority,
        label: priorityName(t, priority),
      })),
      onSelect: (value) => {
        // ConversationHeader's InfoField.onSelect is typed `(value: string) => void`,
        // which erases the option's enum type between `options` and here. Narrow it
        // back rather than sending an unvalidated string at an enum-typed variable.
        if (!isEnumMember(ConversationPriority, value)) return;
        changeConversation({ priority: value });
      },
    },
    {
      id: "status",
      value: conversation
        ? ticketStatusName(
            t,
            ticketStatuses,
            conversation.status,
            conversation.ticketStatusId,
          )
        : undefined,
      valueId: conversation
        ? statusChoiceValue(conversation.status, conversation.ticketStatusId)
        : undefined,
      label: "Status",
      options: statusChoices(ticketStatuses).map((choice) => ({
        value: choice.value,
        label: choice.label,
      })),
      onSelect: (value) => {
        // InfoField.onSelect is typed `(value: string) => void`; read the choice back.
        const picked = choiceOf(statusChoices(ticketStatuses), value);
        if (picked) changeConversation(statusChange(picked));
      },
    },
  ];

  return (
    <>
      <div className={cn("flex w-full", hideEditor ? "relative" : "h-full")}>
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId="customer-ticket"
          className={cn("items-stretch", hideEditor ? "" : "h-full")}
          style={hideEditor ? { height: "auto" } : undefined}
        >
          <ResizablePanel
            id="customer-ticket-compose"
            defaultSize={DEFAULT_LAYOUT[0]}
            order={1}
            style={
              hideEditor
                ? {
                    // Horizontal axis: fill the column. A content-sized basis here
                    // made the column as wide as its header, so picking a longer
                    // assignee name widened the whole messages panel.
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    minWidth: 0,
                    overflow: "hidden",
                  }
                : undefined
            }
          >
            <ResizablePanelGroup
              direction="vertical"
              autoSaveId="customer-ticket-compose"
              style={hideEditor ? { height: "auto" } : undefined}
            >
              <ResizablePanel
                defaultSize={DEFAULT_COMPOSE_LAYOUT[0]}
                order={1}
                style={
                  hideEditor
                    ? {
                        flexGrow: 0,
                        flexShrink: 1,
                        flexBasis: "auto",
                        minWidth: 0,
                        overflow: "hidden",
                      }
                    : undefined
                }
              >
                <section
                  className={cn(
                    "flex flex-col w-full min-w-0",
                    hideEditor ? "" : "h-full",
                  )}
                >
                  {renderInSlot(
                    hideEditor && metaHeader ? (
                      <TicketMetaHeader
                        ticketId={conversation.id}
                        standalone={headerSlot != null}
                        contact={conversation.contact}
                        companyName={conversation.company?.name}
                        title={conversation.title ?? conversation.aiTitle}
                        fields={headerInfoFields}
                        status={conversation.status}
                        priority={conversation.priority}
                        attachments={conversation.attachments}
                        onDownloadAttachment={handleDownloadAttachment}
                        sla={conversation.sla}
                        currentUserId={user.id}
                        working={workingColleague}
                        presence={ownershipEnabled}
                      />
                    ) : (
                      <ConversationHeader
                        standalone={headerSlot != null}
                        person={conversation.contact}
                        personHref={
                          conversation.contact?.id
                            ? `/contacts/${conversation.contact.id}`
                            : undefined
                        }
                        title={conversation.title ?? conversation.aiTitle}
                        summary={hideEditor ? null : conversation.summary}
                        actions={hideEditor ? [] : headerActions}
                        fields={hideEditor ? [] : headerInfoFields}
                        connectorHealth={
                          connectorHealthBadgeEnabled
                            ? conversation.connectorHealth
                            : undefined
                        }
                        detailSection1={
                          hideEditor
                            ? undefined
                            : conversation.company &&
                                [
                                  CompanyType.Retailer,
                                  CompanyType.Distributor,
                                  CompanyType.EndCustomer,
                                ].includes(conversation.company.type)
                              ? {
                                  title:
                                    COMPANY_TYPE[conversation.company.type],
                                  entries: [
                                    {
                                      icon: User,
                                      value: `${conversation.contact.givenName ?? ""} ${conversation.contact.familyName ?? ""}`,
                                    },
                                    {
                                      icon: Building,
                                      value: conversation.company?.name,
                                    },
                                    {
                                      icon: MapPin,
                                      value: conversation.company?.street
                                        ? `${conversation.company.street}${conversation.company.streetNumber ? ` ${conversation.company.streetNumber}` : ""}${conversation.company.postalCode ? `, ${conversation.company.postalCode}` : ""}${conversation.company.city ? ` ${conversation.company.city}` : ""}`
                                        : undefined,
                                    },
                                  ],
                                }
                              : undefined
                        }
                        detailSection2={
                          hideEditor
                            ? undefined
                            : conversation.b2cCustomer
                              ? {
                                  title: "Endkunde",
                                  entries: [
                                    {
                                      icon: User,
                                      value: `${conversation.b2cCustomer.givenName ?? ""} ${conversation.b2cCustomer.familyName ?? ""}`,
                                    },
                                    {
                                      icon: Phone,
                                      value:
                                        conversation.b2cCustomer.phoneNumber,
                                    },
                                  ],
                                }
                              : undefined
                        }
                      />
                    ),
                  )}

                  {/* KI-Assistent öffnen button — hidden when we're already inside the agent view */}
                  {conversation.id && !hideEditor && (
                    <div className="px-4 py-2 border-b border-border/50">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void navigate(`/agent/${conversation.id}`)
                        }
                        className="gap-2"
                      >
                        <Bot className="h-4 w-4" />
                        Im Service Assistant anzeigen
                      </Button>
                    </div>
                  )}

                  <main
                    className={cn("p-2", hideEditor ? "" : "flex-grow min-h-0")}
                  >
                    <ScrollArea
                      className={cn(
                        // Force Radix's viewport content wrapper (a display:table
                        // div by default) to block so a wide HTML-email iframe
                        // can't stretch it past the column and overflow the panel.
                        "w-full px-4 [&_[data-radix-scroll-area-viewport]>div]:!block",
                        hideEditor ? "" : "h-full",
                      )}
                      // Converts `false` to `undefined` so the data attribute is
                      // omitted rather than rendered as "false"; ?? would never
                      // trigger since hideEditor is a boolean, not nullable.
                      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                      data-disable-scroll={hideEditor || undefined}
                    >
                      {translationEnabled && (
                        <div className="flex items-center justify-end gap-2 pb-2">
                          {threadTranslation.failed && (
                            <span
                              role="alert"
                              className="text-xs text-destructive"
                            >
                              {t("threadTranslation.failed")}
                            </span>
                          )}
                          <AnswerLanguageSelect
                            title={t("threadTranslation.title")}
                            languageOptions={threadTranslation.languageOptions}
                            selectedLanguage={
                              threadTranslation.selectedLanguage
                            }
                            isTranslating={threadTranslation.isTranslating}
                            selectLanguage={(code) =>
                              threadTranslation.selectLanguage(code)
                            }
                          />
                        </div>
                      )}
                      <div className="relative space-y-4">
                        {threadTranslation.isTranslating && (
                          <div
                            aria-live="polite"
                            className="absolute inset-0 z-10 flex justify-center pt-8 bg-background/70"
                          >
                            <span className="inline-flex items-center gap-2 h-fit text-sm text-muted-foreground">
                              <Throbber className="h-4 w-4" />
                              {t("threadTranslation.translating")}
                            </span>
                          </div>
                        )}
                        {threadRows.map((item) => {
                          if (item.kind === "shareReply") {
                            return (
                              <ShareReplyBubble
                                key={item.message.id}
                                className={cn(
                                  THREAD_ROW_WIDTH,
                                  THREAD_SIDE_CLASS[threadSide(item.message)],
                                )}
                                root={item.root}
                                message={item.message}
                                viewerId={user.id}
                                agentsById={agentsById}
                                highlightedMessageId={
                                  transientHighlightId ?? highlightedMessageId
                                }
                                ringedMessageId={transientHighlightId}
                                enableNotes={enableNotes}
                                conversationId={conversation.id}
                                onShowMessage={showMessage}
                                onReply={() =>
                                  setReplyingShareId(item.root.id)
                                }
                                onDownloadAttachment={
                                  handleDownloadAttachment
                                }
                              />
                            );
                          }
                          if (item.kind === "thread") {
                            const root = item.root;
                            return (
                              <Fragment key={item.root.id}>
                                <NoteThread
                                  className={cn(
                                    THREAD_ROW_WIDTH,
                                    THREAD_SIDE_CLASS[threadSide(item.root)],
                                  )}
                                  root={item.root}
                                  replies={item.replies}
                                  viewerId={user.id}
                                  agentsById={agentsById}
                                  highlightedMessageId={
                                    transientHighlightId ?? highlightedMessageId
                                  }
                                  ringedMessageId={transientHighlightId}
                                  enableNotes={enableNotes}
                                  conversationId={conversation.id}
                                  describeMessage={describeForwardSource}
                                  onShowMessage={showMessage}
                                  onDownloadAttachment={
                                    handleDownloadAttachment
                                  }
                                  replying={
                                    root.isForward
                                      ? replyingShareId === root.id
                                      : undefined
                                  }
                                  onReplyingChange={
                                    root.isForward
                                      ? (next) =>
                                          setReplyingShareId((current) =>
                                            next
                                              ? root.id
                                              : current === root.id
                                                ? null
                                                : current,
                                          )
                                      : undefined
                                  }
                                />
                              </Fragment>
                            );
                          }
                          const message = item.message;
                          const messageIndex = messages.indexOf(message);
                          const content = threadTranslation.translate(
                            message.id,
                            getMessageContent(message),
                          );
                          const isTranslated = threadTranslation.isTranslated(
                            message.id,
                          );
                          const isOutbound =
                            message.direction === Direction.Outbound;
                          // A connector note or staff-to-staff mail that is not a Lytra
                          // note: keeps its content, reads as internal, offers no reply
                          // (D-16, D-03).
                          const legacyInternal = isInternalRow(message);
                          const showActions =
                            !isOutbound && !hideEditor && !legacyInternal;
                          const rowSurface = legacyInternal
                            ? "border border-dashed border-border bg-transparent"
                            : hideEditor && !chatBubbleLayout
                              ? {
                                  // Agent view: Outlook-style full-width rows so
                                  // the narrow panel doesn't squeeze each message.
                                  "bg-blue-50/50 dark:bg-blue-950/30":
                                    isOutbound,
                                  "bg-primary-subtle border": !isOutbound,
                                }
                              : {
                                  "bg-blue-100 dark:bg-blue-950/50": isOutbound,
                                  "bg-gray-100 dark:bg-muted": !isOutbound,
                                };
                          // Service-assistant context: the ticket title already
                          // carries the first subject, so only a follow-up that
                          // says something else (beyond Re:/Aw:) shows one.
                          const subject =
                            content.emailForwardedSubject ??
                            content.emailSubject;
                          const showSubject = hideAddresses
                            ? messageIndex > 0 &&
                              subjectDiverges(
                                subject,
                                getMessageContent(messages[0]).emailSubject,
                              )
                            : messageIndex === 0;

                          return (
                            <Fragment key={message.id}>
                              <div
                                data-message-id={message.id}
                                className={cn(
                                  // min-w-0 + overflow-hidden keep wide children
                                  // (HTML-email iframes, long unbreakable strings)
                                  // from stretching the bubble past the panel.
                                  // The row sits on its side of the thread, chat-style
                                  // (customer left, we right), capped at 85% of the
                                  // column and a readable 65ch; w-full still lets it
                                  // shrink with a narrow panel.
                                  "gap-4 min-w-0 w-full overflow-hidden whitespace-pre-wrap rounded-2xl p-4 transition-shadow duration-700",
                                  THREAD_ROW_WIDTH,
                                  THREAD_SIDE_CLASS[threadSide(message)],
                                  rowSurface,
                                  {
                                    "ring-2 ring-primary ring-offset-2":
                                      transientHighlightId === message.id,
                                    "ring-1 ring-primary/40":
                                      isTranslated &&
                                      transientHighlightId !== message.id,
                                  },
                                )}
                              >
                                <div className="flex flex-col space-y-2">
                                  {/* Subject — shown once, on the first message;
                                      the timestamp now sits in the sender row below
                                      so there is no empty header row above it. */}
                                  {showSubject && (
                                    <div className="text-md">
                                      {isOutbound ? (
                                        <div className="font-semibold">
                                          {content.emailSubject}
                                        </div>
                                      ) : (
                                        <HoverCard>
                                          <HoverCardTrigger>
                                            <div className="font-semibold">
                                              {content.emailForwardedSubject ??
                                                content.emailSubject}
                                            </div>
                                          </HoverCardTrigger>
                                          <HoverCardContent className="w-96">
                                            <div className="space-y-2">
                                              {message.emailForwardedFromContact ? (
                                                <>
                                                  <div>
                                                    <div className="text-sm font-semibold">
                                                      Ursprünglicher Absender
                                                    </div>
                                                    <div className="text-sm">
                                                      {formatEmailContact(
                                                        message.emailForwardedFromContact,
                                                      )}
                                                    </div>
                                                  </div>
                                                  {message
                                                    .emailForwardedRecipients
                                                    ?.edges.length > 0 && (
                                                    <div>
                                                      <div className="text-sm font-semibold">
                                                        Ursprüngliche Empfänger
                                                      </div>
                                                      <div className="text-sm">
                                                        {message.emailForwardedRecipients.edges
                                                          .map((edge) =>
                                                            formatEmailContact(
                                                              edge.node,
                                                            ),
                                                          )
                                                          .join(", ")}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {message
                                                    .emailForwardedCcRecipients
                                                    ?.edges.length > 0 && (
                                                    <div>
                                                      <div className="text-sm font-semibold">
                                                        Ursprüngliche CC
                                                      </div>
                                                      <div className="text-sm">
                                                        {message.emailForwardedCcRecipients.edges
                                                          .map((edge) =>
                                                            formatEmailContact(
                                                              edge.node,
                                                            ),
                                                          )
                                                          .join(", ")}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {message.emailRecipients.edges
                                                    .length > 0 && (
                                                    <div>
                                                      <div className="text-sm font-semibold">
                                                        Weitergeleitet an
                                                      </div>
                                                      <div className="text-sm">
                                                        {message.emailRecipients.edges
                                                          .map((edge) =>
                                                            formatEmailContact(
                                                              edge.node,
                                                            ),
                                                          )
                                                          .join(", ")}
                                                      </div>
                                                    </div>
                                                  )}
                                                </>
                                              ) : (
                                                <>
                                                  {message.emailRecipients.edges
                                                    .length > 0 && (
                                                    <div>
                                                      <div className="text-sm font-semibold">
                                                        Empfänger
                                                      </div>
                                                      <div className="text-sm">
                                                        {message.emailRecipients.edges
                                                          .map((edge) =>
                                                            formatEmailContact(
                                                              edge.node,
                                                            ),
                                                          )
                                                          .join(", ")}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {message.emailCcRecipients
                                                    .edges.length > 0 && (
                                                    <div>
                                                      <div className="text-sm font-semibold">
                                                        CC
                                                      </div>
                                                      <div className="text-sm">
                                                        {message.emailCcRecipients.edges
                                                          .map((edge) =>
                                                            formatEmailContact(
                                                              edge.node,
                                                            ),
                                                          )
                                                          .join(", ")}
                                                      </div>
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                              {message.emailInternetMessageId && (
                                                <div>
                                                  <div className="text-sm font-semibold">
                                                    Nachrichten-ID
                                                  </div>
                                                  <div className="text-sm break-all">
                                                    {
                                                      message.emailInternetMessageId
                                                    }
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </HoverCardContent>
                                        </HoverCard>
                                      )}
                                    </div>
                                  )}

                                  {/* Sender info and actions */}
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center space-x-2">
                                      <div className="flex flex-col">
                                        <>
                                          <div className="flex items-center gap-2">
                                            {/* On an outgoing card the name is the
                                                recipient; on the others it is the author. */}
                                            {isOutbound && !legacyInternal && (
                                              <span className="text-muted-foreground">
                                                {t("tickets.forward.entry.to")}:
                                              </span>
                                            )}
                                            <SenderName
                                              hideAddress={hideAddresses}
                                              contact={
                                                message.emailForwardedFromContact ??
                                                message.contact ??
                                                message.agent
                                              }
                                            />
                                          </div>
                                          {message.source === Source.Manual && (
                                            <div className="text-muted-foreground text-sm">
                                              {t(
                                                "tickets.newTicket.createdBy",
                                                {
                                                  name:
                                                    personName(
                                                      message.agent ??
                                                        undefined,
                                                    ) ??
                                                    message.agent
                                                      ?.emailAddress ??
                                                    "",
                                                },
                                              )}
                                            </div>
                                          )}
                                          {message.emailForwardedFromContact ? (
                                            <>
                                              <div className="text-muted-foreground text-sm">
                                                <RecipientNames
                                                  hideAddress={hideAddresses}
                                                  contacts={message.emailForwardedRecipients.edges.map(
                                                    (edge) => edge.node,
                                                  )}
                                                />
                                              </div>
                                              <div className="text-muted-foreground text-sm flex items-center gap-1">
                                                <Forward className="w-3 h-3" />
                                                <div>
                                                  <RecipientNames
                                                    hideAddress={hideAddresses}
                                                    contacts={message.emailRecipients.edges.map(
                                                      (edge) => edge.node,
                                                    )}
                                                  />
                                                </div>
                                              </div>
                                            </>
                                          ) : message.direction ===
                                              Direction.Inbound &&
                                            !legacyInternal ? null : (
                                            // An incoming mail went to our own mailbox:
                                            // naming it says nothing.
                                            <div className="text-muted-foreground text-sm">
                                              <RecipientNames
                                                hideAddress={hideAddresses}
                                                contacts={message.emailRecipients.edges.map(
                                                  (edge) => edge.node,
                                                )}
                                              />
                                            </div>
                                          )}
                                        </>
                                      </div>
                                    </div>

                                    {/* Right side: timestamp + actions, on the same
                                        row as the sender. */}
                                    <div className="flex items-center gap-2 shrink-0">
                                      <div className="text-muted-foreground text-sm flex items-center gap-1 flex-wrap">
                                        {!isOutbound &&
                                          message.emailForwardedSentAt && (
                                            <>
                                              <Clock className="w-4 h-4" />
                                              <span>
                                                <RelativeTime
                                                  variant="ago"
                                                  date={
                                                    message.emailForwardedSentAt
                                                  }
                                                />
                                              </span>
                                              <br />
                                              <Forward className="w-4 h-4" />
                                            </>
                                          )}
                                        {DateTime.fromISO(
                                          message.sentAt ??
                                            message.receivedAt ??
                                            message.createdAt,
                                        ).hasSame(
                                          DateTime.fromISO(
                                            message.emailForwardedSentAt ?? "",
                                          ),
                                          "day",
                                        ) ? (
                                          DateTime.fromISO(
                                            message.receivedAt ??
                                              message.sentAt ??
                                              message.createdAt,
                                          ).toFormat("HH:mm 'Uhr'")
                                        ) : (
                                          <RelativeTime
                                            variant="ago"
                                            date={
                                              message.receivedAt ??
                                              message.sentAt ??
                                              message.createdAt
                                            }
                                          />
                                        )}
                                        {message.recoveredFromMessageId && (
                                          <Badge
                                            variant="secondary"
                                            className="ml-2"
                                            title="Diese Nachricht wurde aus dem zitierten Verlauf einer anderen E-Mail rekonstruiert; das Original liegt nicht in der synchronisierten Mailbox."
                                          >
                                            Aus Verlauf wiederhergestellt
                                          </Badge>
                                        )}
                                        <DeliveryProblemBadge
                                          problems={message.deliveryProblems}
                                        />
                                      </div>
                                      {legacyInternal && (
                                        <BadgeV2
                                          tone="info"
                                          size="xs"
                                          icon={Lock}
                                        >
                                          {t("tickets.notes.legacyBadge")}
                                        </BadgeV2>
                                      )}
                                      {/* Message actions */}
                                      <div className="flex items-center space-x-2">
                                        {showActions && (
                                          <div className="flex items-center">
                                            {/*message.aiStatus === MessageAiStatus.Pending && (
                                            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin shrink-0 mr-2" />
                                          )*/}
                                            {userRoles.isLytraStaff && (
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8 text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/10"
                                                disabled={reannotateLoading}
                                                title="KI-Annotation neu starten (Staff)"
                                                onClick={() => {
                                                  void reannotateMessage({
                                                    variables: {
                                                      messageId: message.id,
                                                    },
                                                  });
                                                }}
                                              >
                                                {reannotateLoading ? (
                                                  <Throbber className="w-4 h-4" />
                                                ) : (
                                                  <RotateCw className="w-4 h-4" />
                                                )}
                                              </Button>
                                            )}
                                            <Button
                                              size="icon"
                                              variant={
                                                replyToMessageId ===
                                                  message.id && !replyToAll
                                                  ? "secondary"
                                                  : "ghost"
                                              }
                                              className="h-8 w-8"
                                              onClick={() => {
                                                setReplyToMessageId(message.id);
                                                setReplyToAll(false);
                                              }}
                                            >
                                              <Reply className="w-4 h-4" />
                                            </Button>

                                            {(message.emailRecipients?.edges
                                              .length > 1 ||
                                              message.emailCcRecipients?.edges
                                                .length > 0 ||
                                              message.emailForwardedRecipients
                                                ?.edges.length > 1 ||
                                              message.emailForwardedCcRecipients
                                                ?.edges.length > 0) && (
                                              <Button
                                                size="icon"
                                                variant="ghost"
                                                className={cn("h-8 w-8", {
                                                  "bg-primary text-primary-foreground":
                                                    replyToMessageId ===
                                                      message.id && replyToAll,
                                                })}
                                                onClick={() => {
                                                  setReplyToMessageId(
                                                    message.id,
                                                  );
                                                  setReplyToAll(true);
                                                }}
                                              >
                                                <Forward className="w-4 h-4" />
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <DeliveryProblemNotice
                                    problems={message.deliveryProblems}
                                  />

                                  {/* Message content */}
                                  <MessageContent
                                    emailIsForwarded={message.emailIsForwarded}
                                    emailHasForwarderContent={
                                      message.emailHasForwarderContent
                                    }
                                    content={content}
                                    showFullContent={
                                      showFullContent[message.id]
                                    }
                                    onToggleContent={() =>
                                      toggleMessageContent(message.id)
                                    }
                                    loadRemoteImages={
                                      loadRemoteImages[message.id] ?? false
                                    }
                                    onToggleRemoteImages={() =>
                                      setLoadRemoteImages((prev) => ({
                                        ...prev,
                                        [message.id]: !prev[message.id],
                                      }))
                                    }
                                    hideFullContentToggle={hideEditor}
                                  />
                                  {isTranslated && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                      <Languages className="h-3 w-3" />
                                      {t("threadTranslation.note", {
                                        language: answerLanguageLabel(
                                          message.languageCode ??
                                            threadTranslation.originalLanguage,
                                          i18n.language,
                                        ),
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {message.attachments.edges.filter(
                                ({ node }) => !node.isInline,
                              ).length > 0 && (
                                <div className="mt-2">
                                  <div className="text-sm font-semibold mb-1">
                                    Anhänge:
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <CollapsibleItems
                                      items={message.attachments.edges
                                        .filter(({ node }) => !node.isInline)
                                        .map(({ node }) => node)}
                                    >
                                      {(visible) =>
                                        visible.map((attachment) => (
                                          <div
                                            key={attachment.id}
                                            className="flex items-center gap-2 p-2 rounded-md border text-sm cursor-pointer hover:bg-muted"
                                            onClick={() =>
                                              handleDownloadAttachment(
                                                attachment.id,
                                              )
                                            }
                                          >
                                            {getAttachmentIcon(
                                              attachment.contentType,
                                            )}
                                            <span>{attachment.name}</span>
                                            <span className="text-muted-foreground">
                                              ({formatFileSize(attachment.size)}
                                              )
                                            </span>
                                            {loadingAttachmentIds.has(
                                              attachment.id,
                                            ) && (
                                              <span className="flex items-center gap-1 text-muted-foreground">
                                                <Throbber className="w-4 h-4" />
                                                {t(
                                                  "tickets.attachmentDownload.loading",
                                                )}
                                              </span>
                                            )}
                                          </div>
                                        ))
                                      }
                                    </CollapsibleItems>
                                  </div>
                                </div>
                              )}
                            </Fragment>
                          );
                        })}
                      </div>

                      {enableNotes && (
                        // Internal actions sit on our side, right-aligned. The open
                        // note composer takes a row of its own, still on the right.
                        <div className="mt-6 flex flex-wrap items-start justify-end gap-2 pb-4">
                          <div className="flex justify-end has-[[data-slot=note-composer]]:basis-full">
                            <NoteComposer
                              key={conversation.id}
                              conversationId={conversation.id}
                              variant="note"
                              viewerId={user.id}
                            />
                          </div>
                          {forwardHost.action !== null && (
                            // min-w-0: as a flex item the row would otherwise grow to the
                            // composer's min-content (its one-line subject preview) and,
                            // right-aligned, spill past the column's left edge.
                            <div className="flex min-w-0 justify-end has-[[data-slot=forward-composer]]:basis-full">
                              {forwardHost.action}
                            </div>
                          )}
                          <span role="status" className="sr-only">
                            {noteAnnouncement}
                          </span>
                          {forwardHost.region}
                        </div>
                      )}

                      {!messages && (
                        <div className="mt-40 mx-auto flex flex-col items-center justify-center h-full gap-6">
                          <Sparkles className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                          <div className="space-y-2 text-center">
                            <h2 className="text-xl font-bold">
                              Keine Nachrichten vorhanden.
                            </h2>
                          </div>
                        </div>
                      )}
                    </ScrollArea>
                  </main>
                </section>
              </ResizablePanel>
              {!hideEditor && (
                <>
                  <ResizableHandle />
                  <ResizablePanel
                    defaultSize={DEFAULT_COMPOSE_LAYOUT[1]}
                    minSize={15}
                    order={2}
                  >
                    <div className="flex flex-col h-full">
                      <div className="p-2 border-b flex flex-col">
                        <div className="flex items-center gap-2">
                          <Label className="w-5">Von:</Label>
                          <Select
                            value={selectedEmailAddress}
                            onValueChange={setSelectedEmailAddress}
                          >
                            <SelectTrigger className="w-auto border-none h-8">
                              <SelectValue placeholder="Von E-Mail-Adresse auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {user.emailAddress && (
                                <SelectItem value={user.emailAddress}>
                                  {user.emailAddress} (Persönlich)
                                </SelectItem>
                              )}
                              {tenant.serviceEmailAddress && (
                                <SelectItem value={tenant.serviceEmailAddress}>
                                  {tenant.serviceEmailAddress} (Service)
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        {replyToMessageId && (
                          <div className="flex items-center gap-2">
                            <Label className="w-5">An:</Label>
                            <Select
                              value={
                                replyToAll
                                  ? "all"
                                  : replyToForwarder
                                    ? "forwarder"
                                    : "original"
                              }
                              onValueChange={(value) => {
                                if (value === "all") {
                                  setReplyToAll(true);
                                  setReplyToForwarder(false);
                                } else if (value === "forwarder") {
                                  setReplyToForwarder(true);
                                  setReplyToAll(false);
                                } else {
                                  setReplyToAll(false);
                                  setReplyToForwarder(false);
                                }
                              }}
                            >
                              <SelectTrigger className="w-auto border-none h-8">
                                <SelectValue placeholder="Empfänger auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {(() => {
                                  const replyMessage = messages.find(
                                    (msg) => msg.id === replyToMessageId,
                                  );

                                  if (!replyMessage) return null;

                                  const mainRecipient =
                                    replyMessage.direction ===
                                    Direction.Outbound
                                      ? replyMessage.emailRecipients.edges[0]
                                          ?.node
                                      : replyMessage.emailIsForwarded
                                        ? replyMessage.emailForwardedFromContact
                                        : replyMessage.contact;

                                  const hasMultipleRecipients =
                                    (replyMessage.emailRecipients?.edges
                                      .length || 0) > 1 ||
                                    (replyMessage.emailCcRecipients?.edges
                                      .length || 0) > 0;

                                  return (
                                    <>
                                      <SelectItem value="original">
                                        {formatEmailContact(mainRecipient)}
                                      </SelectItem>

                                      {hasMultipleRecipients && (
                                        <SelectItem value="all">
                                          Alle Empfänger
                                        </SelectItem>
                                      )}

                                      {replyMessage.emailIsForwarded && (
                                        <SelectItem value="forwarder">
                                          {formatEmailContact(
                                            replyMessage.contact,
                                          )}
                                        </SelectItem>
                                      )}
                                    </>
                                  );
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-h-0">
                        {/* Tiptap editor removed - ticket system not in use */}
                        <textarea
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          className="w-full h-full border-none p-5 resize-none focus:outline-none"
                          placeholder="Antwort verfassen..."
                          autoFocus={true}
                        />
                      </div>

                      <div className="p-2 border-t flex justify-between items-center space-x-2">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleSelectAttachments(false)}
                          >
                            <Paperclip className="h-4 w-4" />
                          </Button>

                          {selectedFiles.length > 0 && (
                            <div className="flex gap-2">
                              {selectedFiles.map((file) => (
                                <Badge key={file.file.name} variant="secondary">
                                  {file.file.name}
                                  {file.status === "uploading" && (
                                    <span className="ml-2">
                                      {Math.round(file.progress)}%
                                    </span>
                                  )}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          {replyToMessageId &&
                            (() => {
                              const replyMessage = messages.find(
                                (msg) => msg.id === replyToMessageId,
                              );
                              const isLatestMessage =
                                messages[messages.length - 1]?.id ===
                                replyToMessageId;
                              // `replyToMessageId` only ever comes from `messages`, so the
                              // lookup succeeds today; the fallback keeps the badge
                              // renderable rather than throwing if that stops holding.
                              const content = replyMessage
                                ? getMessageContent(replyMessage)
                                : undefined;

                              return (
                                <Badge variant="outline" className="gap-1">
                                  {replyToAll ? (
                                    <Forward className="w-3 h-3" />
                                  ) : (
                                    <Reply className="w-3 h-3" />
                                  )}
                                  {isLatestMessage
                                    ? `${replyToAll ? "Allen antworten" : "Antwort"} auf letzte Nachricht`
                                    : `${replyToAll ? "Allen antworten" : "Antwort"} auf: ${content?.emailSubject ?? "Nachricht"}`}
                                </Badge>
                              );
                            })()}

                          <Button
                            onClick={handleSendMessage}
                            disabled={
                              !inputMessage.trim() ||
                              !selectedEmailAddress ||
                              !replyToMessageId ||
                              isSending
                            }
                            size="sm"
                          >
                            {isSending ? (
                              <Throbber className="w-4 h-4 mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            {isSending ? "Wird gesendet..." : "Senden"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </ResizablePanel>
                </>
              )}
            </ResizablePanelGroup>
          </ResizablePanel>

          {!hideEditor && (
            <SidePanel
              panelId="customer-ticket-sidebar"
              panelDefaultSize={DEFAULT_LAYOUT[1]}
              panelOrder={2}
              showSmartHistory={showSmartHistory}
              showAfterSales={showAfterSales}
              showAttachments={showAttachments}
              setInputMessage={handleSetInputMessage}
              attachments={conversation.attachments}
              onDownloadAttachment={handleDownloadAttachment}
            />
          )}
        </ResizablePanelGroup>

        {!hideEditor && (
          <PanelNavigation
            toggleSmartHistory={handleToggleSmartHistory}
            toggleAfterSales={handleToggleAfterSales}
            toggleAttachments={handleToggleAttachments}
          />
        )}

        {hideEditor && !metaHeader && conversation.attachments.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="absolute bottom-4 right-4 rounded-full shadow-md h-10 w-10"
                title={`${conversation.attachments.length} Anhänge`}
              >
                <Paperclip className="h-4 w-4" />
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                  {conversation.attachments.length}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-80 max-h-96 overflow-y-auto"
            >
              <div className="space-y-1">
                <h3 className="text-sm font-semibold mb-2">Anhänge</h3>
                <CollapsibleItems
                  items={conversation.attachments}
                  toggleClassName="self-start"
                >
                  {(visible) =>
                    visible.map((attachment) => (
                      <button
                        key={attachment.id}
                        onClick={() => handleDownloadAttachment(attachment.id)}
                        className="flex items-center gap-2 w-full text-left text-xs hover:bg-muted px-2 py-1.5 rounded"
                      >
                        {getAttachmentIcon(attachment.contentType)}
                        <span className="flex-1 truncate">
                          {attachment.name}
                        </span>
                        <span className="text-muted-foreground shrink-0">
                          {formatFileSize(attachment.size)}
                        </span>
                        {loadingAttachmentIds.has(attachment.id) && (
                          <span className="flex items-center gap-1 text-muted-foreground shrink-0">
                            <Throbber className="w-3 h-3" />
                            {t("tickets.attachmentDownload.loading")}
                          </span>
                        )}
                      </button>
                    ))
                  }
                </CollapsibleItems>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </>
  );
}
